import { Link } from 'react-router-dom'
import { useLanguageStore } from '../../store/languageStore'
import { useMenuStore } from '../../store/menuStore'
import { menuItems, MenuItem } from '../../data/menuItems'
import { useState, useMemo } from 'react'
import { FiClock, FiChevronLeft, FiChevronRight, FiRefreshCw } from 'react-icons/fi'
import * as FiIcons from 'react-icons/fi'
import { useModuleStore } from '../../store/moduleStore'
import { isModuleAllowed } from '../../utils/moduleHelpers'

const iconOptions = FiIcons as any

export default function RecentItemsPanel() {
  const { language, direction } = useLanguageStore()
  const isRTL = direction === 'rtl'
  const [isOpen, setIsOpen] = useState(false)
  
  const { 
    recentItems, 
    itemIcons, itemLabels 
  } = useMenuStore()
  const modules = useModuleStore()

  const allMenuItems = useMemo(() => {
    const flatten = (items: MenuItem[]): MenuItem[] => {
      let result: MenuItem[] = []
      items.forEach(item => {
        result.push(item)
        if (item.children) {
          result = result.concat(flatten(item.children))
        }
      })
      return result
    }
    return flatten(menuItems)
  }, [])

  const recentMenuItems = useMemo(() => {
    return recentItems
      .map(path => allMenuItems.find(item => item.path === path))
      .filter((item): item is MenuItem => item !== undefined && (!item.path || isModuleAllowed(item.path, modules)))
      .reverse() // Most recent first
      .slice(0, 10) // Show last 10
  }, [recentItems, allMenuItems, modules])

  if (recentMenuItems.length === 0) return null

  // Position based on opposite of menu (menu is on order: 1)
  // In RTL, order 1 is RIGHT, so panel should be on LEFT
  // In LTR, order 1 is LEFT, so panel should be on RIGHT
  const panelPosition = isRTL ? 'left-0' : 'right-0'
  const toggleButtonPosition = isRTL ? 'left-full ml-2' : 'right-full mr-2'

  return (
    <div 
      className={`fixed top-1/2 -translate-y-1/2 z-[9998] transition-all duration-500 ease-in-out ${panelPosition} ${
        isOpen ? 'translate-x-0' : (isRTL ? '-translate-x-full' : 'translate-x-full')
      }`}
    >
      <div className="relative flex items-center">
        {/* Toggle Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`absolute ${toggleButtonPosition} top-0 p-2 bg-black/60 backdrop-blur-xl border border-white/10 rounded-xl text-white/70 hover:text-white transition-all shadow-2xl group`}
          title={isRTL ? 'العناصر الأخيرة' : 'Recent Items'}
        >
          <div className="flex flex-col items-center gap-1">
            <FiRefreshCw size={20} className={isOpen ? 'text-primary-400' : ''} />
            <span className="text-[10px] font-bold uppercase tracking-widest [writing-mode:vertical-lr] py-2">
              {isRTL ? 'الأخيرة' : 'Recent'}
            </span>
            {isOpen ? (
              isRTL ? <FiChevronLeft size={14} /> : <FiChevronRight size={14} />
            ) : (
              isRTL ? <FiChevronRight size={14} /> : <FiChevronLeft size={14} />
            )}
          </div>
        </button>

        {/* Panel Content */}
        <div className="w-64 max-h-[70vh] overflow-y-auto no-scrollbar bg-black/40 backdrop-blur-2xl border border-white/10 rounded-2xl p-4 shadow-[0_20px_50px_rgba(0,0,0,0.5)]">
          <div className="flex items-center gap-2 mb-4 pb-2 border-b border-white/5">
            <FiClock className="text-primary-400" />
            <span className="text-xs font-bold text-white/40 uppercase tracking-[0.2em]">
              {isRTL ? 'العناصر الأخيرة' : 'Recent Items'}
            </span>
          </div>
          
          <div className="space-y-2">
            {recentMenuItems.map((item, idx) => {
              const Icon = itemIcons[item.path || ''] ? (iconOptions[itemIcons[item.path || '']] || item.icon) : item.icon
              const label = itemLabels[item.path || ''] || item.label[language as keyof typeof item.label] || item.label.en
              
              return (
                <Link
                  key={`${item.path}-${idx}`}
                  to={item.path || '#'}
                  onClick={() => setIsOpen(false)}
                  className={`flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 border border-transparent hover:border-white/5 transition-all group/item ${isRTL ? 'flex-row-reverse text-right' : 'flex-row text-left'}`}
                >
                  <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-white/40 group-hover/item:text-primary-400 group-hover/item:bg-primary-500/20 transition-all flex-shrink-0">
                    {Icon && <Icon size={16} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white/70 group-hover/item:text-white truncate font-medium">
                      {label}
                    </div>
                    <div className="text-[10px] text-white/20 truncate uppercase tracking-tighter">
                      {item.path}
                    </div>
                  </div>
                </Link>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
